import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { type FC, useMemo, useState } from 'react';

import {
  hasRuleEnded,
  ruleDaysLabel,
  ruleRecurrenceLabel,
  ruleTimeRangeLabel,
  ruleTypeLabel,
} from './ruleSummary.js';
import { type MonitoringHoursRule, MonitoringHoursType } from '../../models/index.js';

interface MonitoringHoursRulesListProps {
  rules: MonitoringHoursRule[];
  timeZone?: string;
  isLoading: boolean;
  canEdit: boolean;
  onEditRule: (rule: MonitoringHoursRule) => void;
}

const MonitoringHoursRulesList: FC<MonitoringHoursRulesListProps> = ({
  rules,
  timeZone,
  isLoading,
  canEdit,
  onEditRule,
}) => {
  const [showEnded, setShowEnded] = useState(false);

  const { activeRules, endedRules } = useMemo(
    () => ({
      activeRules: rules.filter((rule) => !hasRuleEnded(rule, timeZone)),
      endedRules: rules.filter((rule) => hasRuleEnded(rule, timeZone)),
    }),
    [rules, timeZone],
  );

  const visibleRules = showEnded ? [...activeRules, ...endedRules] : activeRules;

  if (isLoading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant={'rounded'} height={62} />
        <Skeleton variant={'rounded'} height={62} />
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      <Typography variant={'overline'} color={'text.secondary'}>
        Rules
      </Typography>

      {visibleRules.length === 0 ? (
        <Typography variant={'body2'} color={'text.secondary'}>
          No monitoring rules yet. Add one to start monitoring this patient.
        </Typography>
      ) : null}

      {visibleRules.map((rule) => {
        const hasEnded = hasRuleEnded(rule, timeZone);
        return (
          <Stack
            key={rule.id}
            direction={'row'}
            alignItems={'flex-start'}
            spacing={1.5}
            sx={{
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              opacity: hasEnded ? 0.6 : 1,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                mt: 0.75,
                flexShrink: 0,
                backgroundColor: rule.type === MonitoringHoursType.CHECK_IN ? 'secondary.main' : 'primary.main',
              }}
            />
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant={'body1'} sx={{ fontWeight: 500 }}>
                {ruleDaysLabel(rule, timeZone)} · {ruleTimeRangeLabel(rule, timeZone)}
              </Typography>
              <Typography variant={'body2'} color={'text.secondary'}>
                {ruleTypeLabel(rule)}
                {rule.type === MonitoringHoursType.CHECK_IN ? '' : ` · ${ruleRecurrenceLabel(rule)}`}
                {hasEnded ? ' · ended' : ''}
              </Typography>
            </Stack>
            {canEdit ? (
              <Tooltip arrow title={'Edit rule'}>
                <IconButton
                  size={'small'}
                  sx={{ ml: 'auto' }}
                  aria-label={'Edit rule'}
                  onClick={() => onEditRule(rule)}
                >
                  <EditIcon fontSize={'small'} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        );
      })}

      {endedRules.length > 0 ? (
        <Button size={'small'} sx={{ alignSelf: 'flex-start' }} onClick={() => setShowEnded(!showEnded)}>
          {showEnded ? 'Hide ended' : `Show ended (${endedRules.length})`}
        </Button>
      ) : null}

      <Stack direction={'row'} spacing={2} sx={{ pt: 0.5 }}>
        <Stack direction={'row'} alignItems={'center'} spacing={0.75}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'primary.main' }} />
          <Typography variant={'caption'} color={'text.secondary'}>
            Full monitoring
          </Typography>
        </Stack>
        <Stack direction={'row'} alignItems={'center'} spacing={0.75}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'secondary.main' }} />
          <Typography variant={'caption'} color={'text.secondary'}>
            Check-in
          </Typography>
        </Stack>
      </Stack>

      <Typography variant={'caption'} color={'text.secondary'}>
        Tip: you can also drag directly on the calendar to add a rule.
      </Typography>
    </Stack>
  );
};

export default MonitoringHoursRulesList;
